/**
 * Dial · Gift-history log.
 *
 * Per-occasion gift log: photo (optional), cost, occasion, contact.
 * Encrypted local-only. Drives the "what did I give last year" recall surface
 * for birthdays / anniversaries / Diwali / weddings.
 */
import { store } from '../store.ts';
import { Storage } from '../chassis/index.ts';
import type { Person } from '../types.ts';

interface GiftEntry {
  readonly id: string;
  readonly created_at: string;
  readonly contact_id: string;
  readonly contact_name: string;
  readonly occasion: string;
  readonly cost_inr: number;
  readonly note: string | null;
  readonly photo_data_url: string | null;
}

const FILE = 'dial-gift-log.json';

async function load(): Promise<GiftEntry[]> {
  return (await Storage.readFile<GiftEntry[]>(FILE)) ?? [];
}
async function save(list: GiftEntry[]): Promise<void> {
  await Storage.writeFile(FILE, list);
}

const OCCASIONS = ['Birthday', 'Anniversary', 'Diwali', 'Wedding', 'Eid', 'Christmas', 'Bhai Dooj', 'Raksha Bandhan', 'Promotion', 'Other'] as const;

export class DialGiftLog extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;
  #people: Person[] = [];
  #log: GiftEntry[] = [];

  async connectedCallback(): Promise<void> {
    this.#log = await load();
    this.#unsub = store.subscribe((s) => {
      if (s.summary) {
        this.#people = s.summary.inner_circle.slice(0, 30);
        this.#render();
      }
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  async #addEntry(payload: Omit<GiftEntry, 'id' | 'created_at'>): Promise<void> {
    const entry: GiftEntry = {
      id: crypto.randomUUID(),
      created_at: new Date().toISOString(),
      ...payload,
    };
    this.#log.unshift(entry);
    if (this.#log.length > 200) this.#log.length = 200;
    await save(this.#log);
    this.#render();
  }

  #render(): void {
    const opts = this.#people.map((p) => `<option value="${escapeHtml(p.id)}">${escapeHtml(p.name)}</option>`).join('');
    const occOpts = OCCASIONS.map((o) => `<option value="${o}">${o}</option>`).join('');

    const total = this.#log.reduce((s, g) => s + g.cost_inr, 0);
    const recent = this.#log.slice(0, 6);
    const rows = recent.map((g) => `
      <article class="row">
        <div class="thumb">${g.photo_data_url ? `<img src="${g.photo_data_url}" alt="" />` : `<span class="mark">${escapeHtml(g.contact_name.slice(0, 1).toUpperCase())}</span>`}</div>
        <div class="body">
          <div class="name">${escapeHtml(g.contact_name)}</div>
          <div class="meta">${escapeHtml(g.occasion)} · ${escapeHtml(new Date(g.created_at).toLocaleDateString('en-IN'))}</div>
        </div>
        <div class="cost">₹${g.cost_inr.toLocaleString('en-IN')}</div>
      </article>
    `).join('');

    this.#shadow.innerHTML = `
<style>
:host { display: block; }
.head { padding-left: 4px; margin-bottom: 14px; display: grid; gap: 4px; }
.eyebrow { font-family: var(--mono); font-size: 9px; letter-spacing: 0.42em; text-transform: uppercase; color: var(--ink-faint); }
.title { font-family: var(--display); font-style: italic; font-size: 22px; color: var(--maroon); font-weight: 700; letter-spacing: -0.005em; }
.card { background: var(--paper-up); border: 1px solid var(--rule); border-radius: 8px; padding: 14px 16px; }
form.add { display: grid; grid-template-columns: 1.4fr 1fr 0.6fr auto; gap: 6px; align-items: center; margin-bottom: 14px; }
@container (max-width: 460px) { form.add { grid-template-columns: 1fr 1fr; } }
select, input {
  font-family: var(--mono); font-size: 12px; padding: 10px 12px; border: 1px solid var(--rule);
  background: var(--paper-card); color: var(--ink); border-radius: 6px; letter-spacing: 0.04em;
  outline: none; width: 100%;
}
button.add-btn {
  font-family: var(--mono); font-size: 10px; letter-spacing: 0.28em; text-transform: uppercase;
  padding: 10px 14px; border-radius: 6px; background: var(--maroon); color: var(--paper);
  font-weight: 700; cursor: pointer; border: 0;
}
.kpi { font-family: var(--display); font-size: 32px; color: var(--maroon); font-weight: 700; letter-spacing: -0.02em; line-height: 1; font-feature-settings: 'tnum' 1; margin-bottom: 4px; }
.kpi-label { font-family: var(--mono); font-size: 9px; letter-spacing: 0.3em; text-transform: uppercase; color: var(--ink-faint); padding-bottom: 14px; border-bottom: 1px dashed var(--rule); margin-bottom: 14px; }
.row { display: grid; grid-template-columns: 40px 1fr auto; gap: 12px; align-items: center; padding: 10px 0; border-top: 1px dashed var(--rule); }
.thumb { width: 40px; height: 40px; background: var(--paper-card); border: 1px solid var(--rule); border-radius: 4px; overflow: hidden; display: grid; place-items: center; }
.thumb img { width: 100%; height: 100%; object-fit: cover; }
.mark { font-family: var(--display); font-size: 18px; color: var(--maroon); font-weight: 700; }
.name { font-family: var(--display); font-size: 15px; color: var(--ink); font-weight: 600; letter-spacing: -0.005em; }
.meta { font-family: var(--mono); font-size: 9px; letter-spacing: 0.05em; color: var(--ink-faint); text-transform: lowercase; margin-top: 3px; }
.cost { font-family: var(--display); font-size: 17px; color: var(--ink); font-weight: 700; font-feature-settings: 'tnum' 1; }
.empty { font-family: var(--display); font-style: italic; color: var(--ink-faint); font-size: 15px; padding: 10px 0; }
</style>
<header class="head">
  <span class="eyebrow">Gift log · saheli ka tohfa, padosi ki shaadi</span>
  <h3 class="title">Tohfon ka khaata.</h3>
</header>
<div class="card">
  <form class="add" id="form">
    <select name="contact" required>${opts}</select>
    <select name="occasion" required>${occOpts}</select>
    <input name="cost" type="number" min="0" step="50" placeholder="₹" required />
    <button class="add-btn" type="submit">Add</button>
  </form>
  <div class="kpi">₹${total.toLocaleString('en-IN')}</div>
  <div class="kpi-label">cumulative tohfa</div>
  ${this.#log.length === 0 ? '<p class="empty">Pehla tohfa log karo.</p>' : rows}
</div>
`;

    const form = this.#shadow.getElementById('form') as HTMLFormElement | null;
    form?.addEventListener('submit', (e) => {
      e.preventDefault();
      const f = new FormData(form);
      const contactId = String(f.get('contact') ?? '');
      const person = this.#people.find((p) => p.id === contactId);
      if (!person) return;
      const cost = Number(f.get('cost') ?? 0);
      if (!Number.isFinite(cost) || cost <= 0) return;
      void this.#addEntry({
        contact_id: person.id,
        contact_name: person.name,
        occasion: String(f.get('occasion') ?? 'Other'),
        cost_inr: cost,
        note: null,
        photo_data_url: null,
      });
      form.reset();
    });
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

customElements.define('dial-gift-log', DialGiftLog);
